import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Star } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import api from "@/lib/api";

export const ReviewDialog = ({ open, onOpenChange, onSubmitted }) => {
  const [content, setContent] = useState("");
  const [rating, setRating] = useState(0);
  const [anonymous, setAnonymous] = useState(true);
  const [busy, setBusy] = useState(false);
  const [existing, setExisting] = useState(null);

  useEffect(() => {
    if (open) {
      api
        .get("/reviews/mine")
        .then(({ data }) => setExisting(data && data.id ? data : null))
        .catch(() => {});
    }
  }, [open]);

  const submit = async () => {
    if (content.trim().length < 3) {
      toast("A few words at least.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/reviews", {
        content: content.trim(),
        rating: rating || null,
        anonymous,
      });
      toast(
        anonymous
          ? `Shared as “${data.archetype}”. Thank you.`
          : "Shared. Thank you."
      );
      setContent("");
      setRating(0);
      onOpenChange(false);
      onSubmitted?.();
    } catch (err) {
      toast(err.response?.data?.detail || "Could not share right now.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="review-dialog"
        className="bg-[#22223B] border border-[rgba(242,236,228,0.12)] text-[var(--mirror-text)] max-w-md"
      >
        <DialogHeader>
          <DialogTitle className="font-serif-display font-light text-2xl">
            Share your reflection
          </DialogTitle>
          <DialogDescription className="text-[var(--mirror-text-3)] text-sm">
            {existing
              ? "You have shared before—this will replace what you wrote."
              : "A few honest words for those still deciding whether to look."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 mt-2">
          <Textarea
            data-testid="review-content-input"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="What did the mirror show you?"
            rows={5}
            className="bg-transparent border-[rgba(242,236,228,0.15)] focus-visible:ring-1 focus-visible:ring-[rgba(242,236,228,0.3)] resize-none text-[var(--mirror-text)]"
          />

          <div className="flex items-center gap-2">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                data-testid={`review-star-${n}`}
                type="button"
                onClick={() => setRating(n === rating ? 0 : n)}
                className="transition-opacity duration-300"
                aria-label={`${n} stars`}
              >
                <Star
                  size={20}
                  className={
                    n <= rating
                      ? "fill-[var(--mirror-accent)] text-[var(--mirror-accent)]"
                      : "text-[var(--mirror-text-3)]"
                  }
                />
              </button>
            ))}
          </div>

          <div className="flex items-center justify-between rounded-xl border border-[rgba(242,236,228,0.1)] px-4 py-3">
            <div>
              <p className="text-sm text-[var(--mirror-text)]">
                {anonymous ? "Share anonymously" : "Share with my name"}
              </p>
              <p className="text-xs text-[var(--mirror-text-3)] mt-0.5">
                {anonymous
                  ? "Mirror will name who you were before, e.g. “The Skeptic.”"
                  : "Your profile name will appear."}
              </p>
            </div>
            <Switch
              data-testid="review-anonymous-toggle"
              checked={anonymous}
              onCheckedChange={setAnonymous}
            />
          </div>

          <button
            data-testid="review-submit-button"
            onClick={submit}
            disabled={busy}
            className="w-full py-3 rounded-full border border-[rgba(242,236,228,0.2)] text-sm tracking-[0.2em] uppercase hover:bg-[rgba(242,236,228,0.06)] transition-colors duration-700 disabled:opacity-40"
          >
            {busy ? "…" : "Share"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
